import React, { useMemo } from 'react';

import { Empty, Form, Select } from 'antd';

import { WrapperSelect } from 'common/components';
import {
  FormItemBoolean,
  FormItemContainer,
  FormItemDatetime,
  FormItemNumber,
  FormItemText,
  GroupElementWrapper,
  HeaderGroupElement,
  FormItemTextArea,
} from 'pages/product-full-view/shared/components';

import { FormItemOverlayList } from './FormItemOverlayItem';
import { FormMultipleNestedValueOverlay } from './FormMultipleNestedValueOverlay';
import { FormSingleNestedValueOverlay } from './FormSingleNestedValueOverlay';

import {
  productDetailActionTypes,
  useProductDetail,
  useProductDetailDispatch,
} from '../ProductDetailContext';

import { useScrollToFoundModule } from '../hooks';

import {
  checkAllergenModuleName,
  checkAllergenProperty,
  getChangedField,
} from '../allergenUtils';

import sortByKey from 'utils/sortByKey';
import { filterModuleProperties } from '../filterModuleUtils';
import { checkTypeOfFormNested, findProductPropertyEnums } from '../utils';

import { NUMERIC_TYPE } from 'static/Constants';
import { LIST_PROPERTY_TYPE_TEXTAREA } from 'pages/product-full-view/constants';
import { checkDisabledOverlayField } from 'pages/product-full-view/components/product-new-detail-view/overlay/utils';

export const ProductDetailOverlayEdit = (props) => {
  const {
    moduleNameProperties,
    productEnums,
    isGroup,
    originalProductModules,
  } = props;

  const filteredModuleNameProperties = useMemo(
    () => filterModuleProperties(moduleNameProperties),
    [moduleNameProperties]
  );

  const { moduleProperties: listProperties = [], moduleGroupProperties = [] } =
    filteredModuleNameProperties;

  const {
    formInstance,
    advancedModule: { foundFieldFullPath },
  } = useProductDetail();

  const dispatch = useProductDetailDispatch();

  useScrollToFoundModule({
    elementByClassName: document.getElementsByClassName(
      `product-detail-view__label-form-item--${foundFieldFullPath}`
    ),
  });

  if (isGroup) {
    return (
      <div style={{ height: '80vh', padding: '6px 10px' }} className='scroller'>
        <Form
          form={formInstance}
          labelCol={{
            flex: '300px',
          }}
          wrapperCol={{
            flex: 'auto',
          }}
          className='product-detail__form'
        >
          {moduleGroupProperties.map((groupItem) => {
            return (
              <GroupElementWrapper
                key={groupItem.nameGroup}
                style={{ padding: 12, margin: '18px 0px' }}
              >
                <HeaderGroupElement
                  header={groupItem.nameGroup}
                  style={{ left: 0 }}
                />

                {groupItem.groupProperties.map((item) => {
                  return (
                    <FormElementOverlay
                      key={item.propertyName}
                      property={item}
                      productEnums={productEnums}
                      originalProductModules={originalProductModules}
                    />
                  );
                })}
              </GroupElementWrapper>
            );
          })}
        </Form>
      </div>
    );
  }

  return (
    <div style={{ height: '80vh', padding: '6px 10px' }} className='scroller'>
      <Form
        form={formInstance}
        labelCol={{
          flex: '300px',
        }}
        wrapperCol={{
          flex: 'auto',
        }}
        className='product-detail__form'
        name='nested-messages'
        onFieldsChange={(changedFields) => {
          if (checkAllergenModuleName(moduleNameProperties?.moduleName)) {
            // The 'allFields' value is an array, and we have updated one of the fields.
            // Therefore, we can retrieve the first field from the array.

            const indexField = changedFields?.[0]?.name;
            const changedField = getChangedField(indexField);

            // We have only validated each field (AllergenTypeCode, Region) in the Allergen section.
            // While deleting a field in the Allergen section will have an impact here.
            if (
              checkAllergenProperty(indexField) &&
              changedField !== 'Allergen'
            ) {
              const changedValueField = changedFields?.[0]?.value;

              dispatch({
                type: productDetailActionTypes.VALIDATE_VALUE_ALLERGEN_FIELD,
                payload: {
                  formInstance,
                  indexField,
                  changedValueField,
                },
              });
            }
          }
          return;
        }}
      >
        {listProperties.map((property) => {
          return (
            <FormElementOverlay
              key={property.propertyName}
              property={property}
              productEnums={productEnums}
              originalProductModules={originalProductModules}
            />
          );
        })}
      </Form>
    </div>
  );
};

export const EmptyFormOverlay = () => {
  return (
    <div
      style={{
        height: '80vh',
        padding: '6px 10px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Empty style={{ margin: 'auto' }} />
    </div>
  );
};

/*
  - Single nested value: childProperties.length > 0 && isArray = false;
    * A value of property is {{...}}


  - Multiple nested values: childProperties.length > 0 && isArray = true;
    * A value of property is [{...}, {...}]

*/

const FormElementOverlay = ({
  property,
  productEnums,
  originalProductModules,
}) => {
  const {
    childProperties,
    isArray,
    propertyType,
    propertyName,
    propertyDisplayName,
    listName,
  } = property;

  const disabled = checkDisabledOverlayField(property.fieldFullPath);

  const { isSingleNestedValue, isMultipleNestedValue } = checkTypeOfFormNested(
    childProperties,
    isArray
  );

  if (isSingleNestedValue) {
    return (
      <FormSingleNestedValueOverlay
        name={propertyName}
        property={property}
        productEnums={productEnums}
        parentNestedIndex={[propertyName]}
        originalProductModules={originalProductModules}
      />
    );
  }

  if (isMultipleNestedValue) {
    return (
      <FormMultipleNestedValueOverlay
        properties={childProperties}
        isArray={isArray}
        name={propertyName}
        label={propertyDisplayName}
        property={property}
        productEnums={productEnums}
        parentNestedIndex={[]}
        originalProductModules={originalProductModules}
      />
    );
  } else {
    if (listName) {
      const propertyEnums = findProductPropertyEnums(listName, productEnums);

      return (
        <FormItemContainer property={property} key={propertyName}>
          <WrapperSelect
            showSearch
            mode={isArray ? 'multiple' : ''}
            placeholder={`Please Select ${propertyDisplayName}`}
            filterOption={(input, option) =>
              option.children?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
            }
            disabled={disabled}
          >
            {sortByKey(propertyEnums, 'enumDisplayName')?.map(
              (property, index) => (
                <Select.Option
                  value={property.enumDisplayName}
                  key={property.enumDisplayName + index}
                >
                  {property.enumDisplayName
                    ? property.enumDisplayName
                    : property.enumDescription}
                </Select.Option>
              )
            )}
          </WrapperSelect>
        </FormItemContainer>
      );
    }

    if (isArray) {
      if (NUMERIC_TYPE.includes(property.propertyType)) {
        return (
          <FormItemOverlayList
            property={property}
            name={property.propertyName}
            className='product-detail-view__overlay-form-list--non-nested'
            variant='number'
            originalProductModules={originalProductModules}
            disabled={disabled}
          />
        );
      } else if (property.propertyType === 'datetime') {
        return (
          <FormItemOverlayList
            property={property}
            name={property.propertyName}
            className='product-detail-view__overlay-form-list--non-nested'
            variant='datetime'
            originalProductModules={originalProductModules}
            disabled={disabled}
          />
        );
      } else {
        return (
          <FormItemOverlayList
            property={property}
            name={property.propertyName}
            className='product-detail-view__overlay-form-list--non-nested'
            variant='text'
            originalProductModules={originalProductModules}
            disabled={disabled}
          />
        );
      }
    }

    if (propertyType === 'string') {
      const textareaProperty =
        LIST_PROPERTY_TYPE_TEXTAREA[property.fieldFullPath?.toLowerCase()];

      if (textareaProperty) {
        return (
          <FormItemTextArea
            property={property}
            style={{ marginBottom: '18px' }}
            disabled={disabled}
            {...textareaProperty}
          />
        );
      }
      return (
        <FormItemText
          property={property}
          labelCol={{ flex: '300px' }}
          disabled={disabled}
        />
      );
    }

    if (NUMERIC_TYPE.includes(propertyType))
      return (
        <FormItemNumber
          property={property}
          labelCol={{ flex: '300px' }}
          disabled={disabled}
        />
      );

    if (propertyType === 'datetime')
      return (
        <FormItemDatetime
          property={property}
          labelCol={{ flex: '300px' }}
          disabled={disabled}
        />
      );

    if (propertyType === 'boolean') {
      return (
        <FormItemBoolean
          property={property}
          labelCol={{ flex: '300px' }}
          disabled={disabled}
        />
      );
    }

    // Default display text input for catching bug from incomplete BE data
    return (
      <FormItemText
        property={property}
        labelCol={{ flex: '300px' }}
        disabled={disabled}
      />
    );
  }
};
